import React from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton, Popover } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import Markdown from 'react-markdown';

import { HELP_GATEWAY_CONFIG_OPTIONS, LDAP_CONFIG_OPTIONS, USER_CREATION } from 'dictionary';

const dictionary = {
  HELP_GATEWAY_CONFIG_OPTIONS,
  LDAP_CONNECTIVITY_METHOD: LDAP_CONFIG_OPTIONS.LDAP_CONNECTIVITY_METHOD,
  LDAP_URL: LDAP_CONFIG_OPTIONS.LDAP_URL,
  LDAP_CUSTOM_CA_CERT: LDAP_CONFIG_OPTIONS.LDAP_CUSTOM_CA_CERT,
  LDAP_CLIENT_CA_CERT: LDAP_CONFIG_OPTIONS.LDAP_CLIENT_CA_CERT,
  LDAP_CREATE_SECURITY_POLICY: LDAP_CONFIG_OPTIONS.LDAP_CREATE_SECURITY_POLICY,
  LDAP_CREATE_SECURITY_GROUP: LDAP_CONFIG_OPTIONS.LDAP_CREATE_SECURITY_GROUP,
  LDAP_CONNECT_EXIST_DB: LDAP_CONFIG_OPTIONS.LDAP_CONNECT_EXIST_DB,
  LDAP_USER_SCHEMA: LDAP_CONFIG_OPTIONS.LDAP_USER_SCHEMA,
  LDAP_AUTH_BIND_DN: LDAP_CONFIG_OPTIONS.LDAP_AUTH_BIND_DN,
  LDAP_AUTH_BASE_DN: LDAP_CONFIG_OPTIONS.LDAP_AUTH_BASE_DN,
  USER_IMPORT_CSV: USER_CREATION.USER_IMPORT_CSV,
};

const popoverPaperSx = {
  padding: '9px 14px',
  maxWidth: 276,
  '& p': {
    fontSize: 14,
    margin: 0,
    '&:not(:last-child)': {
      marginBottom: 1,
    },
  },
  '& strong': {
    wordBreak: 'break-all',
  },
};

const ContextHelpPopOver = ({ helpId, customMDText, trigger = 'click', children }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [popoverId] = React.useState(() => `context-help-popover-${Math.random().toString(36).slice(2)}`);
  const triggers = Array.isArray(trigger) ? trigger : [trigger];
  const open = Boolean(anchorEl);
  const isClickTrigger = triggers.includes('click');
  const isHoverTrigger = triggers.includes('hover');
  const isFocusTrigger = triggers.includes('focus');
  const helpText = customMDText || dictionary[helpId] || '';

  const openPopover = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const closePopover = () => {
    setAnchorEl(null);
  };

  const handleClick = (event) => {
    if (!isClickTrigger) return;

    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const handleKeyDown = (event) => {
    if (!isClickTrigger || (event.key !== 'Enter' && event.key !== ' ')) return;

    event.preventDefault();
    handleClick(event);
  };

  const triggerNode = customMDText ? (
    <Box
      component="span"
      role={isClickTrigger ? 'button' : undefined}
      tabIndex={isClickTrigger ? 0 : undefined}
      aria-describedby={open ? popoverId : undefined}
      onClick={isClickTrigger ? handleClick : undefined}
      onKeyDown={isClickTrigger ? handleKeyDown : undefined}
      onMouseEnter={isHoverTrigger ? openPopover : undefined}
      onMouseLeave={isHoverTrigger ? closePopover : undefined}
      onFocus={isFocusTrigger ? openPopover : undefined}
      onBlur={isFocusTrigger ? closePopover : undefined}
      sx={{ display: 'inline-flex' }}
    >
      {children}
    </Box>
  ) : (
    <IconButton
      aria-label="Context help"
      aria-describedby={open ? popoverId : undefined}
      onClick={isClickTrigger ? handleClick : undefined}
      onMouseEnter={isHoverTrigger ? openPopover : undefined}
      onMouseLeave={isHoverTrigger ? closePopover : undefined}
      onFocus={isFocusTrigger ? openPopover : undefined}
      onBlur={isFocusTrigger ? closePopover : undefined}
      size="small"
    >
      <HelpIcon fontSize="small" />
    </IconButton>
  );

  return (
    <>
      {triggerNode}
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{ sx: popoverPaperSx }}
      >
        <Box>
          <Markdown linkTarget="blank" source={helpText} />
        </Box>
      </Popover>
    </>
  );
};

ContextHelpPopOver.propTypes = {
  helpId: PropTypes.string,
  customMDText: PropTypes.string,
  trigger: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  children: PropTypes.node,
};

export default ContextHelpPopOver;
